"use client";

import { useEffect } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { useAutoShowHubUpdates } from "@/components/content/HubUpdates";
import { HubUpdatesModal } from "@/components/content/HubUpdates";
import { BridgeModal } from "@/components/features/bridge";
import { CashbackDialog } from "@/components/features/cashback/CashbackDialog";
import { AntiBotRefreshClearanceModal } from "@/components/features/notifications/AntiBot/AntiBotRefreshClearanceModal";
import { OnboardingModal } from "@/components/features/onboarding/Onboarding";
import { DepositOnCexModal } from "@/components/features/portfolio/DepositOnCexModal";
import { WheelDialog } from "@/components/features/spinning-wheel";
import { ConnectAccount } from "@/components/features/wallet/ConnectAccount";
import { AnimatedCedeLogo } from "@/components/icons";
import { Footer } from "@/components/layout/footer";
import { Header } from "@/components/layout/header";
import { Menu } from "@/components/layout/navigation/menu/Menu";
import { TelegramBackButton } from "@/components/layout/platform/telegram/TelegramBackButton";
import { SidebarNav } from "@/components/layout/sidebar/SidebarNav";
import { DeferredModal } from "@/components/shared/DeferredModal";
import { DevAuthModal } from "@/components/shared/DevAuthModal";
import { DevHelper } from "@/components/shared/DevHelper";
import { AppErrorBoundary } from "@/components/shared/ErrorBoundary/AppErrorBoundary";
import { BanBanner, KrakenReconnectRequiredModal, SubAccountRemovedModal } from "@/components/shared/modals";
import { RemoveExchangeAccountModal } from "@/components/shared/modals/RemoveExchangeAccountModal";
import { SidebarProvider } from "@/components/ui/sidebar";
import { ROUTES } from "@/constants/routes";
import { ALL_THEME_CLASSES, THEME_CLASS } from "@/constants/theme";
import { useTelegramContext } from "@/contexts/TelegramContext";
import {
  useHubUser,
  useDevAuth,
  useOAuthFlow,
  useTrackWalletConnect,
  useRefreshAntibotClearanceSetup,
  useAutoRemoveExchangeAccountForSecurityReason,
  useDevice,
  useAutoTmaSocialLinking,
  useTrackRedirectStatus,
  useDetectExchangeAccountDisconnection,
  useDetectWalletAddressChange,
  useSupportedExchanges,
  useIsImpersonating,
  useIsStandalonePage,
} from "@/hooks";
import { usePreviousPageTracker } from "@/hooks/data/previousPageTracker";
import { useMetricsEvents, useMetricsEventsTransport } from "@/hooks/data/useMetricsEvents";
import { useOnboarding } from "@/hooks/onboarding";
import { useReferralAutoSubmit } from "@/hooks/referral/useReferralAutoSubmit";
import { useBridgeModalStore } from "@/store/bridgeModalStore";
import { useCashbackDialogStore } from "@/store/cashbackDialogStore";
import { useConnectStore } from "@/store/connectStore";
import { useDepositOnCexModalStore } from "@/store/depositOnCexModalStore";
import { useLayoutChromeStore } from "@/store/layoutChromeStore";
import { useSpinningWheelDialogStore } from "@/store/spinningWheelDialogStore";
import { useActiveTheme } from "@/store/themeStore";
import { useAutoRemoveExchangeAccountStore } from "@/store/useAutoRemoveExchangeAccountStore";
import { usePathname } from "next/navigation";

interface AppShellProps {
  children: React.ReactNode;
}

/**
 * AppShell handles all application-level logic:
 * - Authentication and authorization
 * - Global modals (hub updates, spinning wheel, cashback, bridge, etc.)
 * - Tracking and analytics hooks
 * - Security features (antibot, wallet detection)
 * - Navigation components (sidebar, header, footer, menu)
 *
 * It wraps the page content with the necessary providers and logic,
 * but delegates layout (padding, margins) to PageLayout or individual pages.
 */
export const AppShell = ({ children }: AppShellProps) => {
  // Preload supported exchanges
  useSupportedExchanges();
  const { isImpersonating } = useIsImpersonating();

  const { hubUser } = useHubUser();
  const { isMobile, isMounted } = useDevice();
  useMetricsEventsTransport();
  const { trackEvent } = useMetricsEvents();
  const { isTMA } = useTelegramContext();
  const pathname = usePathname();
  const isLaunchPage = pathname?.startsWith(ROUTES.LAUNCH);
  const isStandalonePage = useIsStandalonePage();
  const { hideChrome, hideMobileNav } = useLayoutChromeStore();

  const { shouldAutoSubmit, handleAutoSubmit } = useReferralAutoSubmit();
  const { isOpen: isOnboardingOpen, closeOnboarding } = useOnboarding();

  useOAuthFlow();

  // Single source of truth for DOM theme class management
  const activeTheme = useActiveTheme();
  useEffect(() => {
    const html = document.documentElement;
    ALL_THEME_CLASSES.forEach((cls) => html.classList.remove(cls));
    const themeClass = THEME_CLASS[activeTheme as keyof typeof THEME_CLASS];
    if (themeClass) {
      html.classList.add(themeClass);
    }
  }, [activeTheme]);

  const { shouldShow: shouldShowHubUpdates, handleClose: closeHubUpdates } = useAutoShowHubUpdates();
  const { isOpen: isSpinningWheelOpen, handleClose: closeSpinningWheel } = useSpinningWheelDialogStore();
  const { isOpen: isCashbackOpen, handleClose: closeCashback } = useCashbackDialogStore();
  const { isOpen: isBridgeModalOpen, setIsOpen: setBridgeModalOpen, bridgeConfig } = useBridgeModalStore();
  const { isOpen: isDepositOnCexOpen, handleClose: closeDepositOnCex } = useDepositOnCexModalStore();

  // Store hooks for lazy-loaded modals
  const { open: isAutoRemoveExchangeAccountOpen, setOpen: setAutoRemoveExchangeAccountOpen } =
    useAutoRemoveExchangeAccountStore();
  const { openConnectModal, setOpenConnectModal } = useConnectStore();

  const { isDevAuthRequired, isDevAuthPassed, verifyDevPassword, isVerifying, isLoading } = useDevAuth();

  useEffect(() => {
    if (shouldAutoSubmit) {
      handleAutoSubmit();
    }
  }, [shouldAutoSubmit, handleAutoSubmit]);

  useEffect(() => {
    if (hubUser?.defiAddress) {
      localStorage.setItem("defiAddress", hubUser.defiAddress);
    }
  }, [hubUser?.defiAddress]);

  usePreviousPageTracker();
  useTrackWalletConnect();
  useRefreshAntibotClearanceSetup();
  useAutoRemoveExchangeAccountForSecurityReason();
  useAutoTmaSocialLinking();
  useTrackRedirectStatus();
  useDetectExchangeAccountDisconnection();
  useDetectWalletAddressChange();

  if (isDevAuthRequired && !isLoading && !isDevAuthPassed) {
    return <DevAuthModal open={true} isVerifying={isVerifying} onSubmit={verifyDevPassword} />;
  }

  if (isDevAuthRequired && isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <AnimatedCedeLogo width={120} height={120} />
      </div>
    );
  }

  // Show loader until device detection is complete to prevent hydration mismatches
  if (!isMounted) {
    return (
      <main className="flex min-h-screen w-full items-center justify-center">
        <AnimatedCedeLogo width={120} height={120} />
      </main>
    );
  }

  return (
    <>
      {/* Red border overlay when impersonating another wallet */}
      {isImpersonating && <div className="fixed inset-0 z-snackbar pointer-events-none border-4 border-cede-error" />}

      {/* Privy wallet modal styles — uses solid color tokens (Privy renders its own modal) */}
      <style jsx global>{`
        body {
          --privy-color-background: var(--cede-privy-bg);
          --privy-color-foreground-4: var(--cede-privy-border);
          --privy-color-background-2: var(--cede-privy-bg-hover);
          --privy-color-accent: var(--cede-privy-accent);
          --privy-border-radius-lg: var(--radius-large);
        }
        @media (max-width: 768px) {
          body {
            background-color: var(--color-cede-bg-primary);
          }
        }
        #privy-dialog {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
        }
        #privy-modal-content {
          border: 1px solid var(--cede-privy-border);
        }
        #privy-dialog-backdrop {
          backdrop-filter: blur(12px);
          transition: background-color 100ms ease;
          background-color: rgba(0, 0, 0, 0.8);
        }
      `}</style>
      <TelegramBackButton />
      <SidebarProvider
        defaultOpen={true}
        onSidebarToggle={(isOpen: boolean) => {
          trackEvent(METRICS_EVENTS.NAVIGATION_SIDEBAR_TOGGLE, { isOpen });
        }}
      >
        {!isMobile && !isTMA && !isLaunchPage && !hideChrome && <SidebarNav />}
        {!isMobile && !isTMA && !isLaunchPage && !hideChrome && <Header />}

        <AppErrorBoundary>
          {hubUser?.isBanned && <BanBanner />}
          {children}
          <Footer />
          <DevHelper />
        </AppErrorBoundary>

        {(isTMA || isMobile) && !hideMobileNav && <Menu />}
      </SidebarProvider>

      {/* Modals with external state control (deferred mount; code already in bundle) */}
      <DeferredModal component={WheelDialog} open={isSpinningWheelOpen} onClose={closeSpinningWheel} />
      <DeferredModal component={CashbackDialog} open={isCashbackOpen} onClose={closeCashback} />
      {!isStandalonePage && (
        <>
          <DeferredModal component={HubUpdatesModal} open={shouldShowHubUpdates} onClose={closeHubUpdates} />
          <DeferredModal component={OnboardingModal} open={isOnboardingOpen} onClose={closeOnboarding} />
        </>
      )}

      {/* Lazy-loaded modals with store-managed state */}
      <DeferredModal
        component={RemoveExchangeAccountModal}
        open={isAutoRemoveExchangeAccountOpen}
        onClose={() => setAutoRemoveExchangeAccountOpen(false)}
      />
      <DeferredModal component={ConnectAccount} open={openConnectModal} onClose={() => setOpenConnectModal(false)} />
      <DeferredModal
        component={BridgeModal}
        open={isBridgeModalOpen}
        onOpenChange={setBridgeModalOpen}
        {...bridgeConfig}
      />
      {isDepositOnCexOpen && <DepositOnCexModal open={isDepositOnCexOpen} onClose={closeDepositOnCex} />}

      {/* Critical modals - Keep eager loaded but conditionally rendered */}
      <AntiBotRefreshClearanceModal />
      <SubAccountRemovedModal />
      <KrakenReconnectRequiredModal />
    </>
  );
};
